import React from 'react';
import Title from 'lib/src/components/typography/Title';
import Breadcrumb from '@components/breadcrumb/Breadcrumb';
import { useParams } from 'react-router';
import DataCheck from 'lib/src/components/common/DataCheck';
import useFetchSingleassessmentChallenge from './_hooks/useFetchAssessmentChallenge';
import { isEmpty } from 'lib/src/utils/generic';
import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import dayjs from 'dayjs';
import { assessmentChallenges } from '@constants/breadcrumbItems';
import config from '../../config';
import { DescriptionSectionTypes } from '../../types/shared/Challenge';
import { descriptionComponents } from '@constants/lookups';

const AssessmentChallengeDetails: React.FC = () => {
    const { id } = useParams<ParamsType>();
    const { assessmentChallenge, isFetching, fetchError } = useFetchSingleassessmentChallenge(+id);

    return (
        <>
            <DataCheck
                dataExists={!isEmpty(assessmentChallenge)}
                isFetching={isFetching}
                error={fetchError}
            >
                <Breadcrumb
                    items={assessmentChallenges}
                    title={
                        typeof assessmentChallenge.name === 'string' ? assessmentChallenge.name : id
                    }
                />

                <Title>Challenge Single</Title>
                <p style={{ marginBottom: '1em' }}>
                    View the details of the challenge '
                    <strong>{`${assessmentChallenge.name || '-'}`}</strong>'
                </p>
                <ContentBlock>
                    <ContentRow>
                        <ContentItem label="Name">
                            <p>{`${assessmentChallenge.name || '-'}`}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Start date">
                            <p>{`${
                                typeof assessmentChallenge.startDate === 'string'
                                    ? dayjs(assessmentChallenge.startDate).format('DD/MM/YYYY')
                                    : assessmentChallenge.startDate
                            }`}</p>
                        </ContentItem>
                        <ContentItem label="End date">
                            <p>{`${
                                typeof assessmentChallenge.endDate === 'string'
                                    ? dayjs(assessmentChallenge.endDate).format('DD/MM/YYYY')
                                    : assessmentChallenge.endDate
                            }`}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Tags">
                            <p>{`${
                                Array.isArray(assessmentChallenge.tags)
                                    ? assessmentChallenge.tags.map(t => t.name).join(', ')
                                    : '-'
                            }`}</p>
                        </ContentItem>
                        <ContentItem label="Business tags">
                            <p>{`${
                                Array.isArray(assessmentChallenge.businessTags)
                                    ? assessmentChallenge.businessTags.map(t => t.name).join(', ')
                                    : '-'
                            }`}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Grace period">
                            <p>{`${assessmentChallenge.gracePeriod || '-'}`}</p>
                        </ContentItem>
                        <ContentItem label="Response date">
                            <p>{`${
                                typeof assessmentChallenge.responseDate === 'string'
                                    ? dayjs(assessmentChallenge.responseDate).format('DD/MM/YYYY')
                                    : assessmentChallenge.responseDate
                            }`}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Partner logo">
                            <img
                                src={`${config.S3_URL}/${assessmentChallenge.partnerLogoS3Key}`}
                                alt="partner logo"
                            />
                        </ContentItem>
                        <ContentItem label="Partner logo second">
                            {assessmentChallenge.partnerSecondLogoS3Key ? (
                                <img
                                    src={`${config.S3_URL}/${assessmentChallenge.partnerSecondLogoS3Key}`}
                                    alt="partner logo second"
                                />
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Partner logo third">
                            {assessmentChallenge.partnerThirdLogoS3Key ? (
                                <img
                                    src={`${config.S3_URL}/${assessmentChallenge.partnerThirdLogoS3Key}`}
                                    alt="partner logo third"
                                />
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                        <ContentItem label="Thumbnail image">
                            {assessmentChallenge.imageS3Key ? (
                                <img
                                    src={`${config.S3_URL}/${assessmentChallenge.imageS3Key}`}
                                    alt="Thumbnail"
                                />
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Summary image 1">
                            {assessmentChallenge.summaryImage1S3Key ? (
                                <img
                                    src={`${config.S3_URL}/${assessmentChallenge.summaryImage1S3Key}`}
                                    alt="Summary"
                                />
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                        <ContentItem label="Summary image 2">
                            {assessmentChallenge.summaryImage1S3Key ? (
                                <img
                                    src={`${config.S3_URL}/${assessmentChallenge.summaryImage1S3Key}`}
                                    alt="Second summary"
                                />
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Background">
                            {assessmentChallenge.background ? (
                                assessmentChallenge.background.split('\n').map((paragraph, i) => (
                                    <p key={i} className="background-paragraph">
                                        {paragraph}
                                    </p>
                                ))
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Description sections">
                            {assessmentChallenge.descriptionSections?.length ? (
                                [...assessmentChallenge.descriptionSections]
                                    .sort((a, b) => a.sort - b.sort)
                                    .map((section, i) => {
                                        const SpecificComponent =
                                            descriptionComponents[section.type];

                                        const data =
                                            section.type === DescriptionSectionTypes.Accordion
                                                ? section
                                                : section.contents[0];
                                        return (
                                            <ContentRow key={i}>
                                                <ContentItem
                                                    label={`Section Title - ${
                                                        section.title || '-'
                                                    }`}
                                                    className="description-section"
                                                >
                                                    {/* @ts-ignore */}
                                                    <SpecificComponent data={data} />
                                                </ContentItem>
                                            </ContentRow>
                                        );
                                    })
                            ) : (
                                <p>-</p>
                            )}
                        </ContentItem>
                    </ContentRow>
                </ContentBlock>
            </DataCheck>
        </>
    );
};

interface ParamsType {
    id: string;
}

export default AssessmentChallengeDetails;
